/// <reference types="vite/client" />

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.getAuthHeader(),
      ...(options.headers as Record<string, string> || {}),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'An error occurred');
    }

    return data;
  }

  // Auth endpoints
  async requestOTP(email: string) {
    return this.request<{
      success: boolean;
      message: string;
      isExistingUser: boolean;
      expiresIn: number;
    }>('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyOTP(data: {
    email: string;
    otp: string;
    fullName?: string;
    phone?: string;
    companyName?: string;
  }) {
    return this.request<{
      success: boolean;
      token: string;
      user: User;
      isNewUser: boolean;
    }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendOTP(email: string) {
    return this.request<{
      success: boolean;
      message: string;
      expiresIn: number;
    }>('/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async getCurrentUser() {
    return this.request<User>('/auth/me');
  }

  async updateProfile(data: { fullName: string; phone?: string; companyName?: string }) {
    return this.request<{ success: boolean; user: User }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Domain endpoints
  async checkDomainAvailability(name: string, tld: string) {
    return this.request<{
      available: boolean;
      domain: string;
      tld: string;
      price: number;
    }>(`/domain/check?name=${encodeURIComponent(name)}&tld=${encodeURIComponent(tld)}`);
  }

  async getDomainPricing() {
    return this.request<{
      tlds: Array<{
        tld: string;
        price: number;
        renewalPrice: number;
        transferPrice: number;
      }>;
    }>('/domain/pricing');
  }

  async getMyDomains() {
    return this.request<{
      domains: Domain[];
    }>('/user/domains');
  }

  async getDomainDetail(id: string) {
    return this.request<Domain>(`/domain/${id}`);
  }

  async updateNameservers(domainId: string, nameservers: string[]) {
    return this.request<{ success: boolean }>(`/user/domains/${domainId}/nameservers`, {
      method: 'PUT',
      body: JSON.stringify({ nameservers }),
    });
  }

  async updateAutoRenew(domainId: string, autoRenew: boolean) {
    return this.request<{ success: boolean }>(`/user/domains/${domainId}/auto-renew`, {
      method: 'PUT',
      body: JSON.stringify({ autoRenew }),
    });
  }

  async getDomainById(id: string) {
    return this.request<Domain>(`/user/domains/${id}`);
  }

  // Cart endpoints
  async getCart() {
    return this.request<{
      items: CartItem[];
      subtotal: number;
      total: number;
    }>('/cart');
  }

  async addToCart(data: { domainName: string; tld: string; action: string; years: number; price: number }) {
    return this.request<{ success: boolean; item: CartItem }>('/cart', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCartItem(id: string, years: number) {
    return this.request<{ success: boolean }>(`/cart/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ years }),
    });
  }

  async removeFromCart(id: string) {
    return this.request<{ success: boolean }>(`/cart/${id}`, {
      method: 'DELETE',
    });
  }

  // Order endpoints
  async getOrders() {
    return this.request<{
      orders: Order[];
    }>('/user/orders');
  }

  async getOrderDetail(id: string) {
    return this.request<Order>(`/user/orders/${id}`);
  }

  // Checkout
  async initiateCheckout() {
    return this.request<{
      success: boolean;
      orderId: string;
      paymentForm: {
        action: string;
        fields: Record<string, string>;
      };
    }>('/checkout', {
      method: 'POST',
    });
  }

  // Admin endpoints
  async getAdminStats() {
    return this.request<{
      totalRevenue: number;
      ordersToday: number;
      activeCustomers: number;
      pendingDocuments: number;
      recentOrders: Order[];
    }>('/admin/stats');
  }

  async getAdminOrders(params?: { status?: string; search?: string }) {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return this.request<{
      orders: AdminOrder[];
    }>(`/admin/orders${query ? `?${query}` : ''}`);
  }

  async updateOrderStatus(orderId: string, status: string) {
    return this.request<{ success: boolean }>(`/admin/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async getAdminDocuments() {
    return this.request<{
      documents: AdminDocument[];
    }>('/admin/documents');
  }

  async approveDocument(documentId: string) {
    return this.request<{ success: boolean }>(`/admin/documents/${documentId}/approve`, {
      method: 'PUT',
    });
  }

  async rejectDocument(documentId: string, reason: string) {
    return this.request<{ success: boolean }>(`/admin/documents/${documentId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async getAdminCustomers() {
    return this.request<{
      customers: AdminCustomer[];
    }>('/admin/customers');
  }

  async getAdminSettings() {
    return this.request<IntegrationSettings>('/admin/settings');
  }

  async updateAdminSettings(data: Record<string, string>) {
    return this.request<{ success: boolean }>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

// Types
export interface User {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  companyName?: string;
  isAdmin: boolean;
  emailVerified?: boolean;
  createdAt?: string;
}

export interface Domain {
  id: string;
  domainName: string;
  tld: string;
  status: 'pending' | 'active' | 'expired';
  registrationDate: string;
  expiryDate: string;
  autoRenew: boolean;
  nameservers?: string[];
}

export interface CartItem {
  id: string;
  domainName: string;
  tld: string;
  action: 'register' | 'transfer';
  years: number;
  price: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  orderType: string;
  items: Array<{
    domain: string;
    tld: string;
    type: string;
    years: number;
    price: number;
  }>;
  subtotal: number;
  total: number;
  status: 'pending' | 'paid' | 'processing' | 'completed' | 'cancelled' | 'refunded';
  createdAt: string;
  paidAt?: string;
  completedAt?: string;
}

export interface AdminOrder extends Order {
  user: {
    id: string;
    email: string;
    fullName: string;
  };
}

export interface AdminDocument {
  id: string;
  userId: string;
  domainId: string;
  domain: string;
  documentType: string;
  fileName: string;
  status: 'pending' | 'approved' | 'rejected';
  adminNotes?: string;
  createdAt: string;
  user: {
    email: string;
    fullName: string;
  };
}

export interface AdminCustomer {
  id: string;
  email: string;
  fullName: string;
  phone?: string;
  companyName?: string;
  domainsCount: number;
  ordersCount: number;
  totalSpent: number;
  createdAt: string;
}

export interface IntegrationSettings {
  skipcash: {
    environment: string;
    clientId: { configured: boolean };
    keyId: { configured: boolean };
    keySecret: { configured: boolean };
    webhookKey: { configured: boolean };
  };
  unitech: {
    apiUrl: string;
    username: string;
    apiKey: { configured: boolean };
    resellerId: string;
  };
}

export const api = new ApiClient(API_BASE_URL);
